import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Alert,
    useTheme,
    Paper,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    IconButton,
} from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import {
    getValdIntegrations,
    verifyValdCredentials,
    deleteValdIntegration,
} from '../integrations.api';
import { ValdIntegration } from '../integrations.types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AddIcon from '@mui/icons-material/Add';
import TimelineIcon from '@mui/icons-material/Timeline';
import DeleteIcon from '@mui/icons-material/Delete';
import CreateIntegrationDialog from '../components/CreateIntegrationDialog';
import IntegrationImportDialog from '../components/IntegrationImportDialog';

const IntegrationsList = () => {
    const theme = useTheme();
    const [integrations, setIntegrations] = useState<ValdIntegration[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedIntegration, setSelectedIntegration] = useState<ValdIntegration | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [testResult, setTestResult] = useState<{
        show: boolean;
        success: boolean;
        message: string;
    }>({
        show: false,
        success: false,
        message: '',
    });

    const fetchIntegrations = async () => {
        setIsLoading(true);
        try {
            const response = await getValdIntegrations();
            setIntegrations(response.data.data);
        } catch (error) {
            console.error('Failed to fetch integrations', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchIntegrations();
    }, []);

    const handleTestConnection = async () => {
        setIsTesting(true);
        setTestResult({ show: false, success: false, message: '' });

        try {
            const response = await verifyValdCredentials();
            const { success, credentials_valid, api_ready } = response.data;

            if (success && credentials_valid && api_ready) {
                setTestResult({
                    show: true,
                    success: true,
                    message: 'VALD connection successful! API is ready and credentials are valid.',
                });
            } else if (!api_ready) {
                setTestResult({
                    show: true,
                    success: false,
                    message: 'VALD API is not ready. Please try again later.',
                });
            } else if (!credentials_valid) {
                setTestResult({
                    show: true,
                    success: false,
                    message: 'VALD credentials are invalid. Please check your configuration.',
                });
            } else {
                setTestResult({
                    show: true,
                    success: false,
                    message: 'VALD connection test failed. Please check your configuration.',
                });
            }
        } catch (error: any) {
            console.error('Failed to test connection', error);
            const errorMessage =
                error?.response?.data?.message ||
                'Failed to test VALD connection. Please try again.';
            setTestResult({
                show: true,
                success: false,
                message: errorMessage,
            });
        } finally {
            setIsTesting(false);
        }
    };

    const handleViewImports = (integration: ValdIntegration) => {
        setSelectedIntegration(integration);
        setImportDialogOpen(true);
    };

    const handleDeleteClick = (integration: ValdIntegration) => {
        setSelectedIntegration(integration);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedIntegration) return;

        setIsDeleting(true);
        try {
            await deleteValdIntegration(selectedIntegration.uuid);
            setTestResult({
                show: true,
                success: true,
                message: `Integration for ${selectedIntegration.organization_name} deleted successfully.`,
            });
            setDeleteDialogOpen(false);
            setSelectedIntegration(null);
            fetchIntegrations();
        } catch (error: any) {
            console.error('Failed to delete integration', error);
            const errorMessage =
                error?.response?.data?.message || 'Failed to delete integration. Please try again.';
            setTestResult({
                show: true,
                success: false,
                message: errorMessage,
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const columns: GridColDef<ValdIntegration>[] = [
        {
            field: 'organization_name',
            headerName: 'Organization',
            width: 250,
            sortable: true,
        },
        {
            field: 'tenant_id',
            headerName: 'Tenant ID',
            width: 250,
            sortable: false,
        },
        {
            field: 'api_type',
            headerName: 'API Type',
            width: 100,
            sortable: false,
        },
        {
            field: 'created_at',
            headerName: 'Created At',
            width: 180,
            sortable: true,
            renderCell: (params) => {
                const date = new Date(params.value);
                return date.toLocaleString();
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 200,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<TimelineIcon />}
                        onClick={() => handleViewImports(params.row)}
                    >
                        Imports
                    </Button>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(params.row)}
                        title="Delete Integration"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ margin: theme.spacing(0, 2) }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
                Integrations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: -1 }}>
                Manage VALD Force Decks integrations for organizations
            </Typography>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleTestConnection}
                        disabled={isTesting}
                        startIcon={
                            isTesting ? <CircularProgress size={20} color="inherit" /> : null
                        }
                    >
                        {isTesting ? 'Testing...' : 'Test VALD Connection'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setCreateDialogOpen(true)}
                        startIcon={<AddIcon />}
                    >
                        Create Integration
                    </Button>
                </Box>

                {testResult.show && (
                    <Alert
                        severity={testResult.success ? 'success' : 'error'}
                        icon={
                            testResult.success ? (
                                <CheckCircleIcon fontSize="inherit" />
                            ) : (
                                <ErrorIcon fontSize="inherit" />
                            )
                        }
                        onClose={() => setTestResult({ ...testResult, show: false })}
                        sx={{ mt: 2 }}
                    >
                        {testResult.message}
                    </Alert>
                )}
            </Paper>

            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    VALD Integrations
                </Typography>

                {isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 400,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ height: 600, width: '100%' }}>
                        <DataGridPremium
                            rows={integrations}
                            columns={columns}
                            getRowId={(row) => row.uuid}
                            checkboxSelection={false}
                            disableColumnMenu={true}
                            pageSizeOptions={[10, 25, 50, 100]}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 10, page: 0 },
                                },
                            }}
                            sx={{
                                '& .MuiDataGrid-cell:focus': {
                                    outline: 'none',
                                },
                                '& .MuiDataGrid-cell:focus-within': {
                                    outline: 'none',
                                },
                            }}
                        />
                    </Box>
                )}
            </Paper>

            <CreateIntegrationDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onSuccess={() => {
                    fetchIntegrations();
                }}
            />

            {selectedIntegration && (
                <IntegrationImportDialog
                    open={importDialogOpen}
                    onClose={() => {
                        setImportDialogOpen(false);
                        setSelectedIntegration(null);
                    }}
                    integrationUuid={selectedIntegration.uuid}
                    organizationName={selectedIntegration.organization_name}
                />
            )}

            <Dialog
                open={deleteDialogOpen}
                onClose={() => !isDeleting && setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Integration</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the VALD integration for{' '}
                        <strong>{selectedIntegration?.organization_name}</strong>?
                        <br />
                        <br />
                        This will remove the integration and all associated import tracking data.
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={isDeleting}
                        startIcon={
                            isDeleting ? <CircularProgress size={20} color="inherit" /> : null
                        }
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default IntegrationsList;
